import * as signalR from '@microsoft/signalr';

const retryDelay = ({ previousRetryCount }) => {
    const baseDelay = Math.min(30000, 1000 * (2 ** Math.min(previousRetryCount, 5)));
    return baseDelay + Math.floor(Math.random() * 1000);
};

class SignalRService {
    constructor() {
        this.connection = null;
        this.connectionPromise = null;
        this.messageListeners = new Set();
        this.deletedMessageListeners = new Set();
        this.newChatListeners = new Set();
        this.stateListeners = new Set();
        this.state = 'disconnected';
        this.retryTimer = null;
        this.shouldReconnect = false;
        this.accessTokenFactory = null;
    }

    emitState(state, error = null) {
        this.state = state;
        this.stateListeners.forEach((callback) => callback(state, error));
    }

    async start(accessTokenFactory) {
        if (this.connectionPromise) return this.connectionPromise;
        if (!process.env.REACT_APP_API_URL) throw new Error('REACT_APP_API_URL is not configured');

        const tokenFactory = typeof accessTokenFactory === 'function'
            ? accessTokenFactory
            : async () => accessTokenFactory;
        this.accessTokenFactory = tokenFactory;
        this.shouldReconnect = true;

        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(`${process.env.REACT_APP_API_URL}/chathub`, {
                accessTokenFactory: tokenFactory,
                withCredentials: false,
            })
            .withAutomaticReconnect({ nextRetryDelayInMilliseconds: retryDelay })
            .configureLogging(
                process.env.NODE_ENV === 'production' ? signalR.LogLevel.Warning : signalR.LogLevel.Information,
            )
            .build();

        this.registerListeners();
        this.emitState('connecting');
        this.connectionPromise = this.connection.start()
            .then(() => {
                this.emitState('connected');
                return this.connection;
            })
            .catch((error) => {
                this.connection = null;
                this.connectionPromise = null;
                this.emitState('disconnected', error);
                this.scheduleInitialRetry();
                throw error;
            });

        return this.connectionPromise;
    }

    scheduleInitialRetry() {
        if (!this.shouldReconnect || this.retryTimer || !this.accessTokenFactory) return;
        this.retryTimer = setTimeout(() => {
            this.retryTimer = null;
            this.start(this.accessTokenFactory).catch(() => {});
        }, 5000);
    }

    registerListeners() {
        this.connection.on('ReceiveMessage', (message) => {
            this.messageListeners.forEach((callback) => callback(message));
        });
        this.connection.on('DeleteMessage', (deletedMessageId, topicId) => {
            this.deletedMessageListeners.forEach((callback) => callback(deletedMessageId, topicId));
        });
        this.connection.on('ReceiveNewChat', (chatId, chatTitle) => {
            this.newChatListeners.forEach((callback) => callback(chatId, chatTitle));
        });
        this.connection.onreconnecting((error) => this.emitState('reconnecting', error));
        this.connection.onreconnected(() => this.emitState('connected'));
        this.connection.onclose((error) => {
            this.connection = null;
            this.connectionPromise = null;
            this.emitState('disconnected', error);
            this.scheduleInitialRetry();
        });
    }

    onStateChange(callback) {
        this.stateListeners.add(callback);
        callback(this.state, null);
        return () => this.stateListeners.delete(callback);
    }

    onChatMessage(callback) { this.messageListeners.add(callback); }
    offChatMessage(callback) { this.messageListeners.delete(callback); }
    onDeleteChatMessage(callback) { this.deletedMessageListeners.add(callback); }
    offDeleteChatMessage(callback) { this.deletedMessageListeners.delete(callback); }
    onNewChat(callback) { this.newChatListeners.add(callback); }
    offNewChat(callback) { this.newChatListeners.delete(callback); }

    async stop() {
        this.shouldReconnect = false;
        this.accessTokenFactory = null;
        if (this.retryTimer) clearTimeout(this.retryTimer);
        this.retryTimer = null;
        const connection = this.connection;
        this.connection = null;
        this.connectionPromise = null;
        if (connection) await connection.stop();
        this.emitState('disconnected');
    }
}

const signalRService = new SignalRService();
export default signalRService;
